/**
 * Tiempo del dominio (doc. 03 §2, ADR 0004).
 * Interno: segundos enteros desde las 00:00 del día 1 del horizonte. El día d empieza en
 * 86 400 × (d − 1). En los documentos, las horas del día se guardan como texto «hh:mm».
 */

export type Segundos = number;

export const SEGUNDOS_POR_MINUTO = 60;
export const SEGUNDOS_POR_HORA = 3600;
export const SEGUNDOS_POR_DIA = 86400;

/** «08:30» → 30 600 segundos desde las 00:00. */
export function horaASegundos(hhmm: string): Segundos {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) throw new Error(`Hora no válida: «${hhmm}» (formato hh:mm).`);
  const h = Number(m[1]);
  const min = Number(m[2]);
  if (h > 24 || min > 59 || (h === 24 && min > 0))
    throw new Error(`Hora fuera de rango: «${hhmm}».`);
  return h * SEGUNDOS_POR_HORA + min * SEGUNDOS_POR_MINUTO;
}

export function esHoraValida(hhmm: string): boolean {
  try {
    horaASegundos(hhmm);
    return true;
  } catch {
    return false;
  }
}

/** Día del horizonte (1, 2, …) al que pertenece el instante t. */
export function diaDe(t: Segundos): number {
  return Math.floor(t / SEGUNDOS_POR_DIA) + 1;
}

/** Segundos transcurridos desde las 00:00 del día de t. */
export function segundoDelDia(t: Segundos): Segundos {
  return t - (diaDe(t) - 1) * SEGUNDOS_POR_DIA;
}

/** Instante absoluto de una hora del día d. */
export function instante(dia: number, segundoDelDia: Segundos): Segundos {
  return (dia - 1) * SEGUNDOS_POR_DIA + segundoDelDia;
}

/** 34 500 → «09:35» (ignora el día). */
export function formatoHora(t: Segundos): string {
  const s = ((Math.round(t) % SEGUNDOS_POR_DIA) + SEGUNDOS_POR_DIA) % SEGUNDOS_POR_DIA;
  const h = Math.floor(s / SEGUNDOS_POR_HORA);
  const m = Math.floor((s % SEGUNDOS_POR_HORA) / SEGUNDOS_POR_MINUTO);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/**
 * Suma una duración en horas hábiles (doc. 04 §14.5, decisión P2): solo corre dentro de la ventana
 * diaria [inicio, fin). Si el plazo no alcanza antes del fin, el resto corre desde el inicio del día
 * siguiente. Todos los días del horizonte son hábiles.
 *
 * Ejemplos: 120 min desde las 17:00 con ventana 08:00–18:00 → 09:00 del día siguiente.
 */
export function sumarHorasHabiles(
  desde: Segundos,
  duracion: Segundos,
  inicio: Segundos,
  fin: Segundos,
): Segundos {
  if (!(fin > inicio)) throw new Error('La ventana hábil debe tener inicio anterior al fin.');
  if (duracion < 0) throw new Error('La duración no puede ser negativa.');
  let dia = diaDe(desde);
  let s = segundoDelDia(desde);
  if (s < inicio) s = inicio;
  if (s >= fin) {
    dia += 1;
    s = inicio;
  }
  let restante = duracion;
  for (;;) {
    const disponible = fin - s;
    if (restante <= disponible) return instante(dia, s + restante);
    restante -= disponible;
    dia += 1;
    s = inicio;
  }
}

/** Redondea hacia arriba al múltiplo de `paso` segundos (0 = sin redondeo). */
export function redondearArriba(t: Segundos, paso: Segundos): Segundos {
  if (paso <= 0) return t;
  return Math.ceil(t / paso) * paso;
}
